import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AddConnectorDialog } from "@/components/connectors/AddConnectorDialog";
import { getConnectorLabel } from "@/components/connectors/connector-options";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConnectorsData } from "@/data/use-connectors-data";
import { groupByDate, smartDate } from "@/lib/date";
import type { ConnectorDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faPlug,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";

function statusVariant(
  status: ConnectorDto["status"],
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "error":
      return "destructive";
    case "pending":
      return "secondary";
    default:
      return "outline";
  }
}

export default function ConnectorsPage() {
  const {
    connectors,
    isLoading,
    updateConnector,
    isUpdating,
    deleteConnector,
    isDeleting,
    connectConnector,
    isConnecting,
  } = useConnectorsData();

  const [renameTarget, setRenameTarget] = useState<ConnectorDto | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ConnectorDto | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const groupedConnectors = groupByDate(connectors, "date_created");

  const columns: GroupedTableColumn<ConnectorDto>[] = [
    { key: "custom_name", label: "Name" },
    { key: "provider", label: "Connector" },
    { key: "status", label: "Status" },
    { key: "date_created", label: "Connected" },
  ];

  async function handleRename() {
    if (!renameTarget || !renameValue.trim()) {
      return;
    }

    await updateConnector({
      connectorUuid: renameTarget.uuid,
      customName: renameValue.trim(),
    });
    setRenameTarget(null);
    setRenameValue("");
  }

  async function handleDelete() {
    if (!deleteTarget) {
      return;
    }

    await deleteConnector(deleteTarget.uuid);
    setDeleteTarget(null);
  }

  return (
    <Page>
      <PageHeader>
        <Column className="gap-3">
          <PageHeader.Title>Connectors</PageHeader.Title>
          <p className="text-muted-foreground max-w-2xl text-sm">
            Connect external tools and services to your workspace. Each
            connector is private to you and available across your workbooks.
          </p>
        </Column>
      </PageHeader>
      <PageBody>
        <Column className="gap-4">
          <Row>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <FontAwesomeIcon icon={faPlug} />
              Add connector
            </Button>
          </Row>

          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading connectors…</p>
          ) : connectors.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <FontAwesomeIcon icon={faPlug} className="text-4xl opacity-40" />
              <p className="text-lg font-medium">No connectors yet</p>
              <p className="text-muted-foreground max-w-md text-sm">
                Link an external service to bring its data into your workbooks.
              </p>
            </div>
          ) : (
            <GroupedTable
              data={groupedConnectors}
              columns={columns}
              itemIdKey="uuid"
              renderCell={{
                custom_name: (_, row) =>
                  row.custom_name ?? getConnectorLabel(row.provider),
                provider: (_, row) => getConnectorLabel(row.provider),
                status: (_, row) => (
                  <Badge variant={statusVariant(row.status)}>
                    {row.status}
                  </Badge>
                ),
                date_created: (value) => smartDate(String(value)),
              }}
              renderActions={({ row }) => (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Rename connector"
                    onClick={() => {
                      setRenameTarget(row);
                      setRenameValue(
                        row.custom_name ?? getConnectorLabel(row.provider),
                      );
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete connector"
                    onClick={() => setDeleteTarget(row)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              )}
            />
          )}
        </Column>
      </PageBody>

      <AddConnectorDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onConnect={connectConnector}
        isConnecting={isConnecting}
      />

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTarget(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename connector</DialogTitle>
            <DialogDescription>
              Give this connection a name you&apos;ll recognize later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="connector-name">Name</Label>
            <Input
              id="connector-name"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void handleRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleRename()}
              disabled={!renameValue.trim() || isUpdating}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete connector?</DialogTitle>
            <DialogDescription>
              This removes the connection. Anything you already imported through
              it stays in your workbooks.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
